import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { ProjectPhaseWizard, ProjectPhaseNavigation } from "./components";
import { useProjectQuery, useProjectMutations } from "../../hooks";
import { isPhaseAccessible } from "./projectDetails.definitions";
import type { ProjectPhase } from "../../models";
import { LoadingSpinner, Button } from "@/components";

export default function ProjectDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, error } = useProjectQuery(id!);
  const { updatePhase, isUpdatingPhase } = useProjectMutations();

  const handlePhaseChange = async (newPhase: ProjectPhase) => {
    if (!project) return;
    
    // Verificar se a fase é acessível (agora com dados do projeto)
    if (!isPhaseAccessible(newPhase, project.currentPhase, project)) {
      return;
    }

    try {
      await updatePhase({ id: project.id, phase: newPhase });
    } catch (error) {
      console.error("Erro ao atualizar fase:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-96">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h1 className="text-xl font-semibold text-raro-gray-900 mb-2">
            Projeto não encontrado
          </h1>
          <p className="text-raro-gray-600 mb-4">
            O projeto que você está procurando não existe ou foi removido.
          </p>
          <button
            onClick={() => navigate("/projects")}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Voltar para Projetos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-none mx-auto px-6 py-6 space-y-6">
        {/* Back Button */}
        <div className="flex items-center">
          <Button
            onClick={() => navigate("/projects")}
            variant="secondary"
            size="sm"
            className="mr-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Voltar para Projetos
          </Button>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-montserrat text-raro-gray-900">
              {project.name}
            </h1>
            <p className="text-raro-gray-600 mt-1">
              Acompanhe o desenvolvimento do projeto através das fases
            </p>
          </div>
          
          <Button
            onClick={() => navigate(`/projects/${id}/edit`)}
            variant="secondary"
          >
            Editar Projeto
          </Button>
        </div>

        {/* Project Phase Navigation */}
        <ProjectPhaseNavigation
          project={project}
          currentPhase={project.currentPhase}
          onPhaseChange={handlePhaseChange}
        />

        {/* Project Phase Wizard */}
        {isUpdatingPhase && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              <span className="text-sm text-primary-700">
                Atualizando fase do projeto...
              </span>
            </div>
          </div>
        )}
        
        <ProjectPhaseWizard 
          project={project} 
          onPhaseChange={handlePhaseChange}
        />
      </div>
    </div>
  );
}